import _ from 'lodash';
import moment from 'moment';
import Papa from 'papaparse';
import React, { memo, useContext, useRef, useState } from 'react';
import styled from 'styled-components';
import { Currency, DateTime, Hours, Icon, Percent, RouteLink, Tooltip } from '~/components';
import { weights } from '~/styles';
import ClientApprovalExpensesProgress from '~/components/ClientApprovalExpensesProgress';
import ClientApprovalTimeProgress from '~/components/ClientApprovalTimeProgress';
import ProjectHealthCircle from '~/components/ProjectHealthCircle';
import ProjectHealthScoreBar from '~/components/ProjectHealthScoreBar';
import { MemberContext } from '~/contexts';
import { useNumberFormat } from '~/hooks';
import { dateFormats } from '~/utils';
import { colors } from '~/styles';
import { applyDisplay } from './utils/applyDisplay';
import { expandMoneyFields } from './utils/expandMoneyFields';
import { resolveLink } from './utils/resolveLink';

const fallbackNumberFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fallbackIntegerFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fallbackCurrencyFormat = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fallbackPercentFormat = new Intl.NumberFormat(undefined, {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PREVIEW_ROWS = 3;

// Tooltip listing every reporting currency for a money field. Hovering the cell
// reveals every requested currency (including the one shown in the cell), so the
// reader can see both the primary reporting value and any other targets at once.
const MoneyTooltipBody = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 1rem;
  row-gap: 0.375rem;
  padding: 0.25rem 0;
  font-size: 0.75rem;
  align-items: center;
`;

const MoneyTooltipLabel = styled.div`
  color: ${colors.grey40};
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  font-size: 0.625rem;
`;

const MoneyTooltipValue = styled.div`
  white-space: nowrap;
  text-align: right;
`;

const MoneyCellSpan = styled.span`
  ${({ $interactive }) =>
    $interactive &&
    `
    border-bottom: 0.0625rem dotted ${colors.grey40};
    cursor: help;
  `}
`;

function MultiCurrencyCell({ sources, primary: primaryCurrency, row, maximumFractionDigits }) {
  if (!Array.isArray(sources) || sources.length === 0) return '—';

  // Resolve each source to its currency code and value. A source with a static `currency`
  // is a reporting target; one with a per-row `currencyPath` is the source or a per-row target
  // (project/client) — those carry an optional `label` to name them in the tooltip.
  // Sources whose currency code or value is missing get dropped — that's how cross-currency
  // aggregate rows silently omit the source from the tooltip.
  const entries = sources
    .map((src) => ({
      currency: src.currency || _.get(row, src.currencyPath),
      value: _.get(row, src.path),
      label: src.label,
      isPerRow: !src.currency,
    }))
    .filter((e) => e.value != null && e.currency);

  if (entries.length === 0) return '—';

  // Pick the cell entry by currency code (LLM/caller-controlled via field.money.primary),
  // never by source order. Fall back: first reporting entry → first entry.
  const primary =
    entries.find((e) => primaryCurrency && e.currency === primaryCurrency) ??
    entries.find((e) => !e.isPerRow) ??
    entries[0];
  const cell = (
    <Currency value={primary.value} currency={primary.currency} maximumFractionDigits={maximumFractionDigits} />
  );

  // Tooltip lists every resolved entry (source + reporting) so the reader can
  // see the row's actual currency alongside the converted views. Only shown
  // when there are 2+ entries to compare.
  if (entries.length < 2) return cell;

  return (
    <Tooltip
      delay={350}
      maxWidth="unset"
      message={
        <MoneyTooltipBody>
          {entries.map((entry, i) => (
            <React.Fragment key={`${entry.currency}-${i}`}>
              <MoneyTooltipLabel>{entry.label ?? entry.currency}</MoneyTooltipLabel>
              <MoneyTooltipValue>
                <Currency value={entry.value} currency={entry.currency} maximumFractionDigits={maximumFractionDigits} />
              </MoneyTooltipValue>
            </React.Fragment>
          ))}
        </MoneyTooltipBody>
      }>
      <MoneyCellSpan $interactive>{cell}</MoneyCellSpan>
    </Tooltip>
  );
}

const S = {
  Container: styled.div`
    margin: 0.75rem 0;
    overflow-anchor: none;
  `,

  AuthoredDisclaimer: styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    color: ${colors.warning100};
    background: ${colors.warning10};
    border-radius: 0.375rem;
    margin-bottom: 0.5rem;
  `,

  TableScroll: styled.div`
    overflow-x: auto;
    overflow-y: hidden;
    position: relative;
    transition: max-height 0.3s ease;

    > table {
      min-width: 100%;
      border-collapse: collapse;
      font-size: 0.75rem;
      table-layout: auto;

      th,
      td {
        padding: 0.5rem 0.75rem;
      }

      th {
        white-space: nowrap;
        font-weight: 600;
        background: ${colors.grey5};
        border-bottom: 2px solid ${colors.grey20};
      }

      td {
        border-bottom: 1px solid ${colors.grey10};
        min-width: 10rem;
      }

      .col-narrow {
        min-width: 5rem;
      }

      .col-metric {
        min-width: 7rem;
      }

      .col-wide {
        min-width: 20rem;
      }

      .col-client-approval-progress {
        min-width: 12rem;
      }

      tr:hover {
        background: ${colors.grey5};
      }

      tr:last-child td {
        border-bottom: none;
      }

      .align-right {
        text-align: right;
      }

      .align-center {
        text-align: center;
      }

      .subtotal-row td {
        font-weight: 500;
      }

      .total-row td {
        font-weight: 600;
        border-top: 2px solid ${colors.grey20};
      }
    }
  `,

  FadeOverlay: styled.div`
    position: sticky;
    left: 0;
    height: 4.5rem;
    margin-top: -4.5rem;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
    pointer-events: none;
  `,

  ToggleButton: styled.button`
    border: 1px solid ${colors.grey10};
    border-radius: 999rem;
    background: ${colors.white};
    color: ${colors.grey75};
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    

    &:hover {
      background: ${colors.grey5};
      color: ${colors.black};
    }
  `,

  ButtonRow: styled.div`
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.375rem 0;
    opacity: 0.5;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  `,
};

function columnClass(field) {
  if (field.wide) return 'col-wide';
  if (field.semantic === 'health-status') return 'col-narrow align-center';
  if (field.semantic === 'health-score') return 'col-metric align-center';
  if (field.semantic === 'client-approval-time-progress') return 'col-client-approval-progress';
  if (field.semantic === 'client-approval-expenses-progress') return 'col-client-approval-progress';
  switch (field.type) {
    case 'date':
    case 'date-time':
    case 'boolean':
      return 'col-narrow';
    case 'number':
    case 'integer':
      return 'col-metric align-right';
    default:
      switch (field.role) {
        case 'time':
          return 'col-narrow';
        case 'metric':
          return 'col-metric align-right';
        default:
          return undefined;
      }
  }
}

function nullDisplay(value, field, totalLabel) {
  if (value == null) {
    if (totalLabel != null) return totalLabel;
    if (field.role === 'metric') return '—';
    return '';
  }
  return undefined;
}

function collectionDisplay(value) {
  if (Array.isArray(value)) {
    const parts = value
      .map((v) => {
        if (v == null) return null;
        if (typeof v === 'object') return v.name ?? v.title ?? v.label ?? null;
        return String(v);
      })
      .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  }

  if (typeof value === 'object') {
    return value.name ?? value.title ?? value.label ?? '—';
  }

  return undefined;
}

function commonDisplay(value, field) {
  if (field.type === 'integer') return fallbackIntegerFormat.format(value);
  if (field.type === 'number') return fallbackNumberFormat.format(value);
  if (field.type === 'boolean') return value ? 'Yes' : 'No';
  if (field.enumValues?.[value]) return field.enumValues[value];
  return undefined;
}

function RichCellValue({ value, field, row, totalLabel }) {
  // Match the workspace's default numeric precision (2 decimals for ordinary
  // numbers, 0 for integers) so AI tool output reads the same as the rest of
  // the product. Currency fields may opt into higher precision via
  // `maximumFractionDigits` (e.g. bill/cost rates that need up to 7 decimals).
  const numberFormat = useNumberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const integerFormat = useNumberFormat({ minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // Money fields are virtual descriptors — there's no scalar at field.path. Skip nullDisplay
  // (which would short-circuit on the missing value) and let MultiCurrencyCell read from
  // sources directly. It handles its own empty state when all sources resolve to null.
  if (field.semantic === 'money') {
    return (
      <MultiCurrencyCell
        sources={field.money?.sources}
        primary={field.money?.primary}
        row={row}
        maximumFractionDigits={field.maximumFractionDigits}
      />
    );
  }

  const nd = nullDisplay(value, field, totalLabel);
  if (nd !== undefined) return nd;

  const cd = collectionDisplay(value);
  if (cd !== undefined) return cd;

  if (field.semantic === 'currency') {
    const currency = field.currencyPath ? _.get(row, field.currencyPath) : undefined;
    if (currency) {
      return <Currency value={value} currency={currency} maximumFractionDigits={field.maximumFractionDigits} />;
    }
    return fallbackCurrencyFormat.format(value);
  }

  if (field.semantic === 'hours') return <Hours value={value} />;
  if (field.semantic === 'percent') return <Percent value={value} />;
  if (field.semantic === 'health-status') {
    return (
      <ProjectHealthCircle
        value={{
          id: value,
          label: field.enumValues?.[value] ?? value,
          notes: field.notesPath ? _.get(row, field.notesPath) : undefined,
        }}
      />
    );
  }
  if (field.semantic === 'health-score') return <ProjectHealthScoreBar score={value} />;
  if (field.semantic === 'client-approval-time-progress') return <ClientApprovalTimeProgress clientApproval={row} />;
  if (field.semantic === 'client-approval-expenses-progress')
    return <ClientApprovalExpensesProgress clientApproval={row} />;
  if (field.type === 'date') return <DateTime value={value} format={dateFormats.compactDate} />;
  if (field.type === 'date-time') return <DateTime value={value} format={dateFormats.compactDateTime} />;
  if (field.role === 'time' && field.type === 'string') return moment(value, 'HH:mm:ss').format(dateFormats.shortTime);

  if (field.type === 'integer') return integerFormat.format(value);
  if (field.type === 'number') return numberFormat.format(value);
  if (field.type === 'boolean') return value ? 'Yes' : 'No';
  if (field.enumValues?.[value]) return field.enumValues[value];

  return String(value);
}

function SimpleCellValue({ value, field, row, totalLabel }) {
  // Money fields are virtual descriptors — there's no scalar at field.path. Skip nullDisplay
  // (which would short-circuit on the missing value) and let MultiCurrencyCell read from
  // sources directly. It handles its own empty state when all sources resolve to null.
  if (field.semantic === 'money') {
    return (
      <MultiCurrencyCell
        sources={field.money?.sources}
        primary={field.money?.primary}
        row={row}
        maximumFractionDigits={field.maximumFractionDigits}
      />
    );
  }

  const nd = nullDisplay(value, field, totalLabel);
  if (nd !== undefined) return nd;

  const cd = collectionDisplay(value);
  if (cd !== undefined) return cd;

  if (field.semantic === 'currency') return fallbackCurrencyFormat.format(value);
  if (field.semantic === 'hours') return fallbackNumberFormat.format(value);
  if (field.semantic === 'percent') return fallbackPercentFormat.format(value);

  const cm = commonDisplay(value, field);
  if (cm !== undefined) return cm;

  return String(value);
}

function AssistantTable({ data, fields, display, meta, pagination, filename }) {
  const memberCtx = useContext(MemberContext);
  const CellValue = memberCtx ? RichCellValue : SimpleCellValue;

  const defaultCollapsed = Array.isArray(data) && data.length > 10;
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);
  const tableScrollRef = useRef(null);

  if (!Array.isArray(fields) || fields.length === 0 || !Array.isArray(data) || data.length === 0) return null;

  // Narrow to the LLM's `display` selection (if any). `display` is a Table-only
  // rendering concern — pivot/schedule/chart use the full field catalog.
  const visibleFields = applyDisplay(fields, display);
  const firstDimensionIndex = visibleFields.findIndex((f) => f.role !== 'metric');
  const workspaceKey = meta?.workspaceKey;
  const canCollapse = data.length > PREVIEW_ROWS;
  const visibleRows = isExpanded || !canCollapse ? data : data.slice(0, PREVIEW_ROWS);

  const handleToggle = (expand) => {
    const el = tableScrollRef.current;
    if (!el) {
      setIsExpanded(expand);
      return;
    }

    if (expand) {
      // Pin current height, add rows, animate to new height
      el.style.transition = 'max-height 0.5s ease';
      el.style.maxHeight = `${el.scrollHeight}px`;
      setIsExpanded(true);
      requestAnimationFrame(() => {
        el.style.maxHeight = `${el.scrollHeight}px`;
        // Scroll the table to the top of the chat scroll area
        let scrollParent = el.parentElement;
        while (scrollParent && getComputedStyle(scrollParent).overflowY !== 'auto') {
          scrollParent = scrollParent.parentElement;
        }
        if (scrollParent) {
          const offset = el.parentElement.getBoundingClientRect().top - scrollParent.getBoundingClientRect().top;
          scrollParent.scrollTo({ top: scrollParent.scrollTop + offset, behavior: 'smooth' });
        }
        const onEnd = () => {
          el.style.maxHeight = 'none';
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd);
      });
    } else {
      // Measure collapsed target from the current DOM before removing rows
      const table = el.querySelector('table');
      const thead = table?.querySelector('thead');
      const rows = table?.querySelectorAll('tbody tr');
      let targetHeight = el.scrollHeight;
      if (thead && rows) {
        targetHeight = thead.offsetHeight;
        for (let i = 0; i < Math.min(PREVIEW_ROWS, rows.length); i++) {
          targetHeight += rows[i].offsetHeight;
        }
      }

      // Pin full height, then animate down to collapsed height
      el.style.transition = 'max-height 0.25s ease';
      el.style.maxHeight = `${el.scrollHeight}px`;
      requestAnimationFrame(() => {
        el.style.maxHeight = `${targetHeight}px`;
        const onEnd = () => {
          // Leave maxHeight at targetHeight — content already fits, no visual change.
          // The expand handler will override it on next toggle.
          setIsExpanded(false);
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd);
      });
    }
  };

  const handleDownload = () => {
    // Money fields are virtual descriptors with no row data of their own — expand each into one
    // CSV column per target the rows actually carry (matching the cell tooltip). Shared with the
    // copy/markdown export so both stay in lockstep.
    const exportFields = expandMoneyFields(fields, data);

    const rows = data.map((row) =>
      Object.fromEntries(
        exportFields.map((f) => {
          const header = f.exportLabel ?? f.label;
          const raw = _.get(row, f.path);
          if (raw == null) return [header, ''];
          if (f.enumValues?.[raw]) return [header, f.enumValues[raw]];
          if (Array.isArray(raw))
            return [header, raw.map((v) => (typeof v === 'object' ? (v?.name ?? '') : v)).join(', ')];
          if (typeof raw === 'object') return [header, raw.name ?? raw.title ?? raw.label ?? ''];
          return [header, raw];
        }),
      ),
    );
    const csv = Papa.unparse(rows, { columns: exportFields.map((f) => f.exportLabel ?? f.label) });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename || 'table'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalCount = pagination?.hasMore ? `${data.length}+ rows` : `${data.length} rows`;

  return (
    <S.Container>
      {!meta?.contract && (
        <S.AuthoredDisclaimer>
          <Icon icon="exclamation-triangle" />
          Assembled by the assistant from your data — worth a quick double-check before you rely on it.
        </S.AuthoredDisclaimer>
      )}
      <S.TableScroll ref={tableScrollRef}>
        <table>
          <thead>
            <tr>
              {visibleFields.map((field) => (
                <th key={field.key} className={columnClass(field)}>
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => {
              const isTotal = row.rowType === 'total';
              const isSubtotal = row.rowType === 'subtotal';
              return (
                <tr key={i} className={isTotal ? 'total-row' : isSubtotal ? 'subtotal-row' : undefined}>
                  {visibleFields.map((field, fieldIndex) => {
                    const rawValue = _.get(row, field.path);
                    const link = field.link?.template ? resolveLink(field.link.template, row, workspaceKey) : null;
                    const isDimension = field.role !== 'metric';

                    // Null dimension labels:
                    // - Total row: "Total" (first dimension) / "" (rest)
                    // - Grouped dimension: "None" (labels the null group)
                    // - Non-grouped dimension: blank (handled by CellValue)
                    let totalLabel;
                    if (rawValue == null && isDimension) {
                      if (isTotal) {
                        totalLabel = fieldIndex === firstDimensionIndex ? 'Total' : '';
                      } else if (isSubtotal) {
                        totalLabel = '';
                      } else if (field.grouped) {
                        totalLabel = 'None';
                      }
                    }

                    return (
                      <td key={field.key} className={columnClass(field)}>
                        {link ? (
                          <RouteLink to={link}>
                            <CellValue value={rawValue} field={field} row={row} totalLabel={totalLabel} />
                          </RouteLink>
                        ) : (
                          <CellValue value={rawValue} field={field} row={row} totalLabel={totalLabel} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!isExpanded && canCollapse && <S.FadeOverlay />}
      </S.TableScroll>
      <S.ButtonRow>
        {canCollapse &&
          (isExpanded ? (
            <S.ToggleButton onClick={() => handleToggle(false)}>Show fewer</S.ToggleButton>
          ) : (
            <S.ToggleButton onClick={() => handleToggle(true)}>Show all ({totalCount})</S.ToggleButton>
          ))}
        <S.ToggleButton onClick={handleDownload}>Save CSV</S.ToggleButton>
      </S.ButtonRow>
    </S.Container>
  );
}

export default memo(AssistantTable);
